import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Save, KeyRound, Copy, RefreshCw, Lock, SlidersHorizontal, Loader2, Chrome, CheckCircle2, Plug, Download } from "lucide-react";

export default function Settings() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [s, setS] = useState(null);
  const [token, setToken] = useState(null);
  const [saving, setSaving] = useState(false);
  const [extInstalled, setExtInstalled] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [connected, setConnected] = useState(false);
  const [showToken, setShowToken] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setS(r.data));
    api.get("/auth/api-token").then((r) => setToken(r.data));
  }, []);

  // detect the ApplyPilot extension on this page via its postMessage bridge
  useEffect(() => {
    const onMsg = (ev) => {
      if (ev.source !== window || !ev.data || ev.data.source !== "applypilot-extension") return;
      if (ev.data.type === "PONG") setExtInstalled(true);
      if (ev.data.type === "CONNECTED") {
        setConnecting(false);
        if (ev.data.ok) { setConnected(true); toast.success(`Extension connected · ${ev.data.count} clients`); }
        else toast.error(ev.data.error || "Could not connect the extension");
      }
    };
    window.addEventListener("message", onMsg);
    window.postMessage({ source: "applypilot-dashboard", type: "PING" }, "*");
    const t = setTimeout(() => window.postMessage({ source: "applypilot-dashboard", type: "PING" }, "*"), 800);
    return () => { window.removeEventListener("message", onMsg); clearTimeout(t); };
  }, []);

  const connectExtension = async () => {
    setConnecting(true);
    try {
      let tok = token?.api_token;
      if (!tok) { const { data } = await api.post("/auth/api-token"); tok = data.api_token; setToken({ ...token, api_token: tok }); }
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      window.postMessage({ source: "applypilot-dashboard", type: "CONNECT", apiToken: tok, backendUrl }, "*");
      setTimeout(() => setConnecting(false), 5000);
    } catch (e) { setConnecting(false); toast.error(apiError(e)); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", {
        profile_prompt: s.profile_prompt,
        evaluation_prompt: s.evaluation_prompt,
        thresholds: s.thresholds,
      });
      toast.success("Settings saved");
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const regen = async () => {
    const { data } = await api.post("/auth/api-token");
    setToken({ ...token, api_token: data.api_token });
    toast.success("New extension token generated");
  };

  const copy = () => { navigator.clipboard.writeText(token?.api_token || ""); toast.success("Token copied"); };

  const setTh = (k, v) => setS({ ...s, thresholds: { ...s.thresholds, [k]: v } });

  if (!s) return <div className="text-slate-500 text-sm">Loading…</div>;

  const chip = "inline-block text-[11px] font-mono bg-indigo-500/15 text-indigo-300 border border-indigo-500/30 px-1.5 py-0.5 rounded mr-1 mb-1";

  return (
    <div className="space-y-6 animate-fade-up">
      <div>
        <h1 className="font-heading text-3xl font-bold text-slate-100 tracking-tight">Settings</h1>
        <p className="text-slate-400 text-sm mt-1">Evaluation prompts, hard-rule thresholds, and your extension token.</p>
      </div>

      {/* Connect extension */}
      <div className="bg-[#12151F] border border-slate-800/80 rounded-xl p-5 shadow-lg shadow-black/40">
        <h3 className="font-heading font-semibold text-slate-100 flex items-center gap-2"><Chrome className="w-4 h-4 text-indigo-400" /> Connect browser extension</h3>
        <p className="text-[11px] text-slate-500 mt-1">
          Install the ApplyPilot extension, then click connect here — no need to copy any token into the extension.
        </p>
        <div className="mt-4 flex items-center gap-3 flex-wrap">
          <span data-testid="ext-status" className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border ${extInstalled ? "text-emerald-400 border-emerald-500/30 bg-emerald-500/10" : "text-amber-400 border-amber-500/30 bg-amber-500/10"}`}>
            <span className={`w-2 h-2 rounded-full ${extInstalled ? "bg-emerald-400" : "bg-amber-400"}`} />
            {extInstalled ? "Extension detected" : "Extension not detected"}
          </span>
          {connected && <span className="flex items-center gap-1.5 text-xs text-emerald-400"><CheckCircle2 className="w-4 h-4" /> Connected</span>}
          <a data-testid="download-extension" href="/applypilot-extension.zip" download
            className="ml-auto flex items-center gap-2 text-sm text-slate-100 bg-slate-800/70 hover:bg-slate-800 border border-slate-700/70 px-4 py-2.5 rounded-lg transition-colors">
            <Download className="w-4 h-4" /> Download extension
          </a>
          <button data-testid="connect-extension" onClick={connectExtension} disabled={connecting || !extInstalled}
            className="flex items-center gap-2 text-sm bg-indigo-500 hover:bg-indigo-600 text-white px-4 py-2.5 rounded-lg transition-colors disabled:opacity-50">
            {connecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plug className="w-4 h-4" />} Connect extension
          </button>
        </div>
        {!extInstalled && (
          <p className="text-[11px] text-slate-500 mt-3">
            Don't have it yet? <a href="/applypilot-extension.zip" className="text-indigo-400 hover:text-indigo-300">Download the extension</a>, load it via chrome://extensions (Developer mode → Load unpacked), then refresh this page.
          </p>
        )}
        <button onClick={() => setShowToken((v) => !v)} className="mt-3 text-[11px] text-slate-500 hover:text-slate-300">
          {showToken ? "Hide" : "Advanced:"} manual API token
        </button>
        {showToken && (
          <div className="mt-2 flex items-center gap-3 flex-wrap">
            <code data-testid="api-token-value" className="flex-1 min-w-[240px] bg-[#0D0E15] border border-slate-800 rounded-lg px-3 py-2.5 text-sm text-indigo-300 font-mono truncate">
              {token?.api_token || "No token yet"}
            </code>
            <button data-testid="copy-token" onClick={copy} disabled={!token?.api_token} className="flex items-center gap-2 text-sm text-slate-300 bg-slate-800/60 hover:bg-slate-800 border border-slate-700/60 px-4 py-2.5 rounded-lg transition-colors disabled:opacity-50">
              <Copy className="w-4 h-4" /> Copy
            </button>
            <button data-testid="regen-token" onClick={regen} className="flex items-center gap-2 text-sm bg-indigo-500 hover:bg-indigo-600 text-white px-4 py-2.5 rounded-lg transition-colors">
              <RefreshCw className="w-4 h-4" /> {token?.api_token ? "Regenerate" : "Generate"}
            </button>
          </div>
        )}
      </div>

      {/* Thresholds */}
      <div className="bg-[#12151F] border border-slate-800/80 rounded-xl p-5 shadow-lg shadow-black/40">
        <h3 className="font-heading font-semibold text-slate-100 flex items-center gap-2"><SlidersHorizontal className="w-4 h-4 text-indigo-400" /> Hard-rule thresholds {!isAdmin && <Lock className="w-3.5 h-3.5 text-slate-500" />}</h3>
        <div className="grid sm:grid-cols-2 gap-4 mt-4">
          <div>
            <label className="text-xs text-slate-500">Minimum fit score for a clean APPLY</label>
            <input data-testid="th-min-score" type="number" disabled={!isAdmin} value={s.thresholds.min_fit_score}
              onChange={(e) => setTh("min_fit_score", Number(e.target.value))}
              className="mt-1 w-full bg-[#0D0E15] border border-slate-800 rounded-lg px-3 py-2 text-sm text-slate-200 outline-none focus:border-indigo-500/60 disabled:opacity-60" />
          </div>
          <div>
            <label className="text-xs text-slate-500">Major experience gap (years)</label>
            <input data-testid="th-gap" type="number" disabled={!isAdmin} value={s.thresholds.major_experience_gap_years}
              onChange={(e) => setTh("major_experience_gap_years", Number(e.target.value))}
              className="mt-1 w-full bg-[#0D0E15] border border-slate-800 rounded-lg px-3 py-2 text-sm text-slate-200 outline-none focus:border-indigo-500/60 disabled:opacity-60" />
          </div>
          <div>
            <label className="text-xs text-slate-500">Duplicate warning window (days)</label>
            <input data-testid="th-dupwindow" type="number" disabled={!isAdmin} value={s.thresholds.duplicate_window_days}
              onChange={(e) => setTh("duplicate_window_days", Number(e.target.value))}
              className="mt-1 w-full bg-[#0D0E15] border border-slate-800 rounded-lg px-3 py-2 text-sm text-slate-200 outline-none focus:border-indigo-500/60 disabled:opacity-60" />
          </div>
          <label className="flex items-center gap-3 mt-6">
            <input type="checkbox" data-testid="th-autoreject" disabled={!isAdmin} checked={s.thresholds.auto_reject_no_sponsorship}
              onChange={(e) => setTh("auto_reject_no_sponsorship", e.target.checked)} className="accent-indigo-500 w-4 h-4" />
            <span className="text-sm text-slate-300">Auto-reject when sponsorship required but not offered</span>
          </label>
        </div>
      </div>

      {/* Prompts */}
      <div className="bg-[#12151F] border border-slate-800/80 rounded-xl p-5 shadow-lg shadow-black/40">
        <h3 className="font-heading font-semibold text-slate-100">Evaluation prompt {!isAdmin && <Lock className="inline w-3.5 h-3.5 text-slate-500 ml-1" />}</h3>
        <p className="text-[11px] text-slate-500 mt-1 mb-2">Editable template. Variables:
          <span className="ml-2">
            {["{{candidate_profile}}", "{{resume_text}}", "{{job_title}}", "{{job_company}}", "{{job_description}}", "{{job_salary}}"].map((v) => <span key={v} className={chip}>{v}</span>)}
          </span>
        </p>
        <textarea data-testid="evaluation-prompt" disabled={!isAdmin} rows={12} value={s.evaluation_prompt}
          onChange={(e) => setS({ ...s, evaluation_prompt: e.target.value })}
          className="w-full bg-[#0D0E15] border border-slate-800 rounded-lg px-3 py-2.5 text-xs text-slate-300 font-mono leading-relaxed outline-none focus:border-indigo-500/60 disabled:opacity-60" />
      </div>

      <div className="bg-[#12151F] border border-slate-800/80 rounded-xl p-5 shadow-lg shadow-black/40">
        <h3 className="font-heading font-semibold text-slate-100">Profile-generation prompt {!isAdmin && <Lock className="inline w-3.5 h-3.5 text-slate-500 ml-1" />}</h3>
        <p className="text-[11px] text-slate-500 mt-1 mb-2">Variables: <span className={chip}>{"{{resume_text}}"}</span><span className={chip}>{"{{intake_text}}"}</span></p>
        <textarea data-testid="profile-prompt" disabled={!isAdmin} rows={10} value={s.profile_prompt}
          onChange={(e) => setS({ ...s, profile_prompt: e.target.value })}
          className="w-full bg-[#0D0E15] border border-slate-800 rounded-lg px-3 py-2.5 text-xs text-slate-300 font-mono leading-relaxed outline-none focus:border-indigo-500/60 disabled:opacity-60" />
      </div>

      {isAdmin ? (
        <div className="flex justify-end">
          <button data-testid="save-settings" onClick={save} disabled={saving} className="flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors disabled:opacity-60">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save settings
          </button>
        </div>
      ) : (
        <div className="text-xs text-slate-500 bg-slate-800/40 border border-slate-800 rounded-lg px-4 py-3 flex items-center gap-2">
          <Lock className="w-4 h-4" /> Only team leads / admins can edit prompts and thresholds.
        </div>
      )}
    </div>
  );
}
